import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, FileSpreadsheet, FileText, Search } from "lucide-react";
import { toast } from "sonner";
import { formatCell } from "@/lib/format";
import { exportReport, errMsg } from "@/lib/api";
import { Pager } from "./kit";

const NUMERIC = ["money", "int"];

export default function DataTable({
  columns, rows = [], title = "Report", subtitle = "", searchable = true, exportable = false, totals = [],
  pageSize = 50, onRowClick, testid = "data-table", loading, emptyText = "No records found.", toolbar,
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [q, rows]);

  const filtered = useMemo(() => {
    let r = rows || [];
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => columns.some((c) => String(x[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort.key) {
      r = [...r].sort((a, b) => {
        const av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
        return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir;
      });
    }
    return r;
  }, [rows, q, sort, columns]);

  const view = pageSize ? filtered.slice((page - 1) * pageSize, page * pageSize) : filtered;
  const sums = totals.length ? Object.fromEntries(totals.map((k) => [k, filtered.reduce((s, r) => s + (Number(r[k]) || 0), 0)])) : null;
  const expCols = columns.filter((c) => !c.noExport);

  const doExport = async (fmt) => {
    try {
      const data = filtered.map((r) => Object.fromEntries(expCols.map((c) => [c.key, c.exportValue ? c.exportValue(r) : r[c.key]])));
      if (sums) data.push(Object.fromEntries(expCols.map((c, i) => [c.key, i === 0 ? "TOTAL" : sums[c.key] ?? ""])));
      await exportReport(fmt, title, subtitle, expCols.map((c) => ({ key: c.key, label: c.label })), data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const align = (c) => (c.align === "right" || NUMERIC.includes(c.type) ? "text-right" : "text-left");

  return (
    <div data-testid={testid}>
      {(searchable || exportable || toolbar) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {searchable && (
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-8" data-testid={`${testid}-search`} />
            </div>
          )}
          {toolbar}
          {exportable && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid={`${testid}-export-xlsx`}>
                <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid={`${testid}-export-pdf`}>
                <FileText className="mr-1.5 h-4 w-4" /> PDF
              </Button>
            </div>
          )}
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${align(c)}`}>
                  {c.sortable === false ? c.label : (
                    <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setSort({ key: c.key, dir: sort.key === c.key ? -sort.dir : 1 })}>
                      {c.label} <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
            ) : view.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-slate-500" data-testid={`${testid}-empty`}>{emptyText}</td></tr>
            ) : (
              view.map((r, idx) => (
                <tr key={r.id || r.product_id || r.salesman_id || idx} onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={`transition-colors hover:bg-blue-50/40 ${onRowClick ? "cursor-pointer" : ""}`} data-testid={`${testid}-row-${idx}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`whitespace-nowrap px-3 py-2.5 ${align(c)} ${NUMERIC.includes(c.type) ? "font-mono tabular text-slate-900" : "text-slate-700"}`}>
                      {c.render ? c.render(r) : formatCell(c.type, r[c.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {sums && view.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <tr data-testid={`${testid}-totals`}>
                {columns.map((c, i) => (
                  <td key={c.key} className={`whitespace-nowrap px-3 py-2.5 ${align(c)} ${NUMERIC.includes(c.type) ? "font-mono" : ""}`}>
                    {i === 0 ? "TOTAL" : sums[c.key] !== undefined ? formatCell(c.type, sums[c.key]) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {pageSize > 0 && <Pager page={page} total={filtered.length} limit={pageSize} onPage={setPage} testid={`${testid}-pager`} />}
    </div>
  );
}
